import { useEffect, useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../../contexts/ProjectContext';
import { dashboardService } from '../../../services/dashboard';
import { obfuscateKey } from '../CodeSnippet';

export default function KeysStep({ onHasKeysChange }) {
  const { selectedHash } = useProject();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdToken, setCreatedToken] = useState('');
  const [copied, setCopied] = useState(false);

  const loadKeys = () => {
    if (!selectedHash) return;
    dashboardService.keys(selectedHash, (res) => {
      const list = (res.data || []).filter((k) => k.is_active);
      setKeys(list);
      onHasKeysChange?.(list.length > 0 || !!createdToken);
    }, console.error);
  };

  useEffect(() => {
    loadKeys();
  }, [selectedHash]);

  useEffect(() => {
    onHasKeysChange?.(keys.length > 0 || !!createdToken);
  }, [keys.length, createdToken, onHasKeysChange]);

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const createKey = () => {
    if (!selectedHash) return;
    setSaving(true);
    dashboardService.createKey(
      { name: name.trim() || 'default', project: selectedHash },
      (res) => {
        setCreatedToken(res.data.token);
        sessionStorage.setItem('setup_latest_key', res.data.token);
        setSaving(false);
        setName('');
        loadKeys();
      },
      () => setSaving(false),
    );
  };


  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Create an API key</h1>
      <p className="text-sm opacity-60 mb-8">
        {keys.length > 0
          ? 'Create a new key for this setup, or continue with an existing one below.'
          : 'Your API key authenticates requests to os-router. Copy it now, you will not see the full key again.'}
      </p>

      {createdToken && (
        <div className="mb-6 rounded-xl border border-theme-primary/30 bg-theme-primary/5 p-4">
          <p className="text-sm font-medium mb-2 text-theme-primary">Key created. Copy it now.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs sm:text-sm font-mono break-all bg-theme-main rounded-lg px-3 py-2 border border-theme-secondary">
              {createdToken}
            </code>
            <button
              type="button"
              onClick={copyToken}
              className="shrink-0 p-2 rounded-lg border border-theme-secondary hover:bg-theme-secondary/30"
            >
              {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {keys.length > 0 && (
        <div className="mb-8 space-y-2">
          <p className="text-sm font-medium opacity-70">Your keys</p>
          {keys.map((k) => (
            <div
              key={k.hash}
              className="flex items-center justify-between rounded-xl border border-theme-secondary bg-theme-main px-4 py-3"
            >
              <div>
                <div className="text-sm font-medium">{k.name}</div>
                <div className="font-mono text-xs opacity-50 mt-0.5">{obfuscateKey(k.token_prefix)}</div>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Active</span>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-theme-secondary bg-theme-main p-5">
        <p className="text-sm font-medium opacity-70 mb-3">
          {keys.length > 0 ? 'Create another key' : 'New key'}
        </p>
        <label className="block text-sm opacity-70 mb-2">Key name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. production"
          className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm mb-4"
        />
        <button
          type="button"
          onClick={createKey}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create key'}
        </button>
      </div>
    </div>
  );
}
